import React, { useState, useRef, useEffect } from "react";
import { Container, Form, Row, Col, Button } from "react-bootstrap";
import AlertOverlay from "../../../components/Alert";
import { useNavigate } from "react-router-dom";
import { useClient } from "../../../apis/client/ClientProvider";
import { getUiInterface, getPageName, getWordConnector, getSuccessMessage, getErrorMessage } from "../../../locales/translationUtils";
import { useTranslation } from 'react-i18next';
import NavBar, { NavBarItem } from "../../../components/NavBar";
import Client from "../../../types/client";
import ColorPicker from "../../../components/ColorPicker";

const ClientCreateView: React.FC = () => {
    const { t } = useTranslation();
    const [alert, setAlert] = useState<{
        message: string;
        variant: "success" | "danger" | "warning" | "info";
    } | null>(null);
    const { postClient } = useClient();
    const navigate = useNavigate();
    const [invalidFields, setInvalidFields] = useState<Set<string>>(new Set());

    const descriptionRef = useRef<HTMLInputElement | null>(null);

    const [client, setClient] = useState<Client>({
        id: 0,
        name: "",
        cnpj: "",
        color: "",
        location: {
            id: 0,
            latitude: "",
            longitude: "",
            address: {
                id: 0,
                street: "",
                number: "",
                complement: "",
                city: "",
                state: "",
                country: "",
                zipCode: ""
            }
        }
    } as Client);

    const navItems: NavBarItem[] = [
        { label: t('client.labels.home'), route: "/", iconName: "house-door" },
        { label: t('client.labels.list'), route: "/Client", iconName: "table" },
    ];

    const handleChange = (e: React.ChangeEvent<any>) => {
        const { name, value } = e.target;

        setClient((prevClient) => {
            const keys = name.split(".");
            const updatedClient = { ...prevClient };
            let current: any = updatedClient;

            for (let i = 0; i < keys.length - 1; i++) {
                const key = keys[i];
                current[key] = { ...current[key] };
                current = current[key];
            }

            current[keys[keys.length - 1]] = value;
            
            return updatedClient;
        });
    };

    const hadleChangeColor = (newColor: string) => {
        setClient((prevState) => ({
            ...prevState,
            color: newColor.replaceAll("#", ""),
        }));
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        const nullRequiredFields: string[] = [];

        if (!client.name) nullRequiredFields.push("Name");
        if (!client.location?.latitude) nullRequiredFields.push("Lat");
        if (!client.location?.longitude) nullRequiredFields.push("Lng");
        if (!client.location?.address?.street) nullRequiredFields.push("Street");
        if (!client.location?.address?.number) nullRequiredFields.push("Number");
        if (!client.location?.address?.complement) nullRequiredFields.push("Complement");
        if (!client.location?.address?.city) nullRequiredFields.push("City");
        if (!client.location?.address?.state) nullRequiredFields.push("State");
        if (!client.location?.address?.country) nullRequiredFields.push("Country");
        if (!client.location?.address?.zipCode) nullRequiredFields.push("Zip Code");

        if (nullRequiredFields.length > 0) {
            setInvalidFields(new Set(nullRequiredFields));
            const warnMessage: string =
                nullRequiredFields.length === 1
                    ? getUiInterface("fieldRequiredSingle", {
                        field: nullRequiredFields[0],
                    })
                    : getUiInterface("fieldRequiredMultiple", {
                        fields: `${nullRequiredFields.slice(0, -1).join(", ")}${nullRequiredFields.length > 1
                            ? ` ${getWordConnector("and")} `
                            : ""
                            }${nullRequiredFields.slice(-1)}`,
                    });

            setAlert({ message: warnMessage, variant: "danger" });
            return;
        } else {
            setInvalidFields(new Set());
        }

        try {
            const response = await postClient(client);

            if (response?.messages == null && response?.data?.id) {
                const successMessage = getSuccessMessage("createClient", { id: response.data.id });
                navigate(`/Client`, {
                    state: {
                        message: successMessage,
                        variant: "success",
                    },
                });
            } else {
                setAlert({
                    message: getErrorMessage("createClientError", { error: response?.messages }),
                    variant: "danger",
                });
            }
        } catch (error) {
            setAlert({
                message: getErrorMessage("genericError", {}),
                variant: "danger",
            });
        }
    };

    useEffect(() => {
        if (descriptionRef.current) {
            descriptionRef.current.focus();
        }
    }, []);

    return (
        <Container fluid>
            <NavBar items={navItems} />
            {alert && (
                <AlertOverlay
                    message={alert.message}
                    variant={alert.variant}
                    onClose={() => setAlert(null)}
                />
            )}

            <div className="mb-3"></div>
            <h2>{getPageName("createClient")}</h2>
            <Form 
                onSubmit={handleSubmit}
                style={{paddingBottom: "32px"}}
            >
                <Row>
                    <Col md={4} className="mb-3">
                        <Form.Group controlId="name">
                            <Form.Label>{t('client.fields.name')}</Form.Label>
                            <Form.Control
                                type="text"
                                name="name"
                                value={client.name}
                                isInvalid={invalidFields.has("Name")}
                                onChange={handleChange}
                                style={{ width: "100%" }}
                            />
                        </Form.Group>
                    </Col>

                    <Col md={4} className="mb-3">
                        <Form.Group controlId="cnpj">
                            <Form.Label>{t('client.fields.cnpj')}</Form.Label>
                            <Form.Control
                                type="text"
                                name="cnpj"
                                value={client.cnpj}
                                onChange={handleChange}
                                style={{ width: "100%" }}
                            />
                        </Form.Group>
                    </Col>

                    <Col md={4} className="mb-3">
                        <Form.Group controlId="color">
                            <Form.Label>{t('client.fields.color')}</Form.Label>
                            <div style={{display: "flex", alignItems: "center"}}>
                                <Form.Control
                                    type="text"
                                    name="color"
                                    value={client.color}
                                    onChange={handleChange}
                                    style={{ 
                                        width: "calc(100% - 64px)", 
                                        display: "inline-block", 
                                        marginRight: "8px" 
                                    }}
                                />
                                <ColorPicker
                                    color={`#${client.color}`}
                                    onChange={(newColor) =>
                                        hadleChangeColor(newColor)
                                    }
                                    blocked={false}
                                />
                            </div>
                        </Form.Group>
                    </Col>
                </Row>

                <Row>
                    <h5>{t('client.labels.location')}</h5>
                    <Col md={6} className="mb-3">
                        <Form.Group controlId="lat">
                            <Form.Label>{t('client.fields.lat')}</Form.Label>
                            <Form.Control
                                type="text"
                                name="location.latitude"
                                value={client.location?.latitude}
                                isInvalid={invalidFields.has("Lat")}
                                onChange={handleChange}
                                style={{ width: "100%" }}
                            />
                        </Form.Group>
                    </Col>
                    <Col md={6} className="mb-3">
                        <Form.Group controlId="lng">
                            <Form.Label>{t('client.fields.lng')}</Form.Label>
                            <Form.Control
                                type="text"
                                name="location.longitude"
                                value={client.location?.longitude}
                                isInvalid={invalidFields.has("Lng")}
                                onChange={handleChange}
                                style={{ width: "100%" }}
                            />
                        </Form.Group>
                    </Col>
                </Row>

                <Row>
                    <h6>{t('client.labels.address')}</h6>
                    <Col md={5} className="mb-3">
                        <Form.Group controlId="street">
                            <Form.Label>{t('client.fields.street')}</Form.Label>
                            <Form.Control
                                type="text"
                                name="location.address.street"
                                value={client.location?.address?.street}
                                isInvalid={invalidFields.has("Street")}
                                onChange={handleChange}
                                style={{ width: "100%" }}
                            />
                        </Form.Group>
                    </Col>
                    <Col md={2} className="mb-3">
                        <Form.Group controlId="number">
                            <Form.Label>{t('client.fields.number')}</Form.Label>
                            <Form.Control
                                type="text"
                                name="location.address.number"
                                value={client.location?.address?.number}
                                isInvalid={invalidFields.has("Number")}
                                onChange={handleChange}
                                style={{ width: "100%" }}
                            />
                        </Form.Group>
                    </Col>
                    <Col md={5} className="mb-3">
                        <Form.Group controlId="complement">
                            <Form.Label>{t('client.fields.complement')}</Form.Label>
                            <Form.Control
                                type="text"
                                name="location.address.complement"
                                value={client.location?.address?.complement}
                                isInvalid={invalidFields.has("Complement")}
                                onChange={handleChange}
                                style={{ width: "100%" }}
                            />
                        </Form.Group>
                    </Col>
                </Row>

                <Row>
                    <Col md={6} className="mb-3">
                        <Form.Group controlId="city">
                            <Form.Label>{t('client.fields.city')}</Form.Label>
                            <Form.Control
                                type="text"
                                name="location.address.city"
                                value={client.location?.address?.city}
                                isInvalid={invalidFields.has("City")}
                                onChange={handleChange}
                                style={{ width: "100%" }}
                            />
                        </Form.Group>
                    </Col>
                    <Col md={6} className="mb-3">
                        <Form.Group controlId="state">
                            <Form.Label>{t('client.fields.state')}</Form.Label>
                            <Form.Control
                                type="text"
                                name="location.address.state"
                                value={client.location?.address?.state}
                                isInvalid={invalidFields.has("State")}
                                onChange={handleChange}
                                style={{ width: "100%" }}
                            />
                        </Form.Group>
                    </Col>
                </Row>

                <Row>
                    <Col md={6} className="mb-3">
                        <Form.Group controlId="country">
                            <Form.Label>{t('client.fields.country')}</Form.Label>
                            <Form.Control
                                type="text"
                                name="location.address.country"
                                value={client.location?.address?.country}
                                isInvalid={invalidFields.has("Country")}
                                onChange={handleChange}
                                style={{ width: "100%" }}
                            />
                        </Form.Group>
                    </Col>
                    <Col md={6} className="mb-3">
                        <Form.Group controlId="zipCode">
                            <Form.Label>{t('client.fields.zipCode')}</Form.Label>
                            <Form.Control
                                type="text"
                                name="location.address.zipCode"
                                value={client.location?.address?.zipCode}
                                isInvalid={invalidFields.has("Zip Code")}
                                onChange={handleChange}
                                style={{ width: "100%" }}
                            />
                        </Form.Group>
                    </Col>
                </Row>

                <Button variant="primary" onClick={handleSubmit}>
                    {getUiInterface("create")}
                </Button>
            </Form>
        </Container>
    );
};

export default ClientCreateView;